import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SectionTitle from '../components/SectionTitle';

const IdeasPage: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Demo ideas data - MOVED UP
  const ideas = [
    {
      id: 1,
      title: 'Own Booking System for Restaurants',
      description: 'A simple reservation system for restaurants that can be directly integrated into their own website and has no monthly fees.',
      status: 'inProgress',
      votes: 42,
      category: 'tool',
      industry: 'gastronomy',
      date: '2023-09-15'
    },
    {
      id: 2,
      title: 'Open Source Alternative to Lieferando & Co.',
      description: 'A common ordering system for local restaurants that does not charge high commissions and strengthens the direct relationship with the customer.',
      status: 'planned',
      votes: 76,
      category: 'platform',
      industry: 'gastronomy',
      date: '2023-08-22'
    },
    {
      id: 3,
      title: 'Simple Portfolio Website for Creatives',
      description: 'A way for artists, designers, and other creatives to showcase their work without being dependent on large platforms.',
      status: 'completed',
      votes: 38,
      category: 'tool',
      industry: 'creatives',
      date: '2023-10-05'
    },
    {
      id: 4,
      title: 'Common Marketplace for Local Retailers',
      description: 'A platform that brings local retailers together but remains in their ownership – without high fees or dependencies.',
      status: 'received',
      votes: 51,
      category: 'platform',
      industry: 'retail',
      date: '2023-11-12'
    },
    {
      id: 5,
      title: 'Modular Coaching Booking System',
      description: 'A system for coaches and consultants to manage their appointments without being dependent on expensive platforms.',
      status: 'inProgress',
      votes: 29,
      category: 'tool',
      industry: 'coaches',
      date: '2023-10-18'
    },
    {
      id: 6,
      title: 'Connected Open Source Alternatives',
      description: 'An initiative to connect existing open-source alternatives to commercial platforms and make them more easily accessible.',
      status: 'onHold',
      votes: 63,
      category: 'initiative',
      industry: 'all',
      date: '2023-09-01'
    },
  ];

  const [ideasData, setIdeasData] = useState(
    ideas.map(idea => ({ ...idea, userVoted: false }))
  );
  
  // Idea status options
  const statusTypes = [
    { id: 'all', name: 'All Ideas' },
    { id: 'received', name: 'Received', color: 'bg-blue-100 text-blue-800' },
    { id: 'planned', name: 'Planned', color: 'bg-purple-100 text-purple-800' },
    { id: 'inProgress', name: 'In Progress', color: 'bg-yellow-100 text-yellow-800' },
    { id: 'completed', name: 'Completed', color: 'bg-green-100 text-green-800' },
    { id: 'onHold', name: 'On Hold', color: 'bg-gray-100 text-gray-800' }
  ];

  // Filter and search ideas
  const filteredIdeas = ideasData
    .filter(idea => activeFilter === 'all' || idea.status === activeFilter)
    .filter(idea => 
      searchTerm === '' || 
      idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Get status details for an idea
  const getStatusDetails = (statusId: string) => {
    const status = statusTypes.find(s => s.id === statusId);
    return status || statusTypes[0];
  };

  // Dummy handleVote function to avoid errors and simulate voting
  const handleVote = (ideaId: number) => {
    setIdeasData(prevIdeas => 
      prevIdeas.map(idea => 
        idea.id === ideaId ? { ...idea, userVoted: !idea.userVoted, votes: idea.userVoted ? idea.votes -1 : idea.votes + 1 } : idea
      )
    );
  };

  return (
    <main className="pt-24 pb-16">
      {/* Header Section */}
      <section className="bg-primary/5 py-16">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <SectionTitle 
            title="Ideas & Roadmap"
            subtitle="Submit your ideas, vote for existing ones, and see what we're planning."
            centered
          />
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 border-b border-gray-200">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {statusTypes.map((status) => (
                <button
                  key={status.id}
                  onClick={() => setActiveFilter(status.id)}
                  className={`px-3 py-1 rounded-md text-sm font-lexend transition-all ${
                    activeFilter === status.id
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status.name}
                </button>
              ))}
            </div>
            
            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search ideas..."
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ideas List */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="space-y-6">
            {filteredIdeas.length > 0 ? (
              filteredIdeas.map((idea) => {
                const statusDetails = getStatusDetails(idea.status);
                
                return (
                  <div 
                    key={idea.id} 
                    className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-all"
                  >
                    <div className="flex justify-between items-start flex-wrap gap-4">
                      <h3 className="font-lexend font-bold text-xl text-dark">
                        {idea.title}
                      </h3>
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded ${statusDetails.color}`}>
                        {statusDetails.name}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mt-3 mb-4">
                      {idea.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-3 mb-4">
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {idea.industry === 'all' ? 'All' : idea.industry}
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {idea.category === 'tool' ? 'Tool' : 
                          idea.category === 'platform' ? 'Platform' : 'Initiative'}
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        Submitted: {new Date(idea.date).toLocaleDateString('en-US')}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-1">
                        <button 
                          onClick={() => handleVote(idea.id)}
                          className={`px-4 py-2 rounded-md text-sm font-lexend transition-all flex items-center space-x-2 ${idea.userVoted ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.865.802V10.333z"></path>
                          </svg>
                          <span>{idea.userVoted ? 'Voted' : 'Upvote'}</span>
                        </button>
                        <span className="text-sm text-gray-500">{idea.votes} Votes</span>
                      </div>
                      
                      <button className="text-primary hover:underline font-lexend font-medium text-sm">
                        Details & Comments
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <h3 className="font-lexend font-semibold text-xl text-gray-700 mb-3">
                  No ideas match your current filter or search.
                </h3>
                <p className="text-gray-500">
                  Try adjusting your filters or submit a new idea!
                </p>
              </div>
            )}
          </div>
          
          {filteredIdeas.length > 0 && (
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600 mb-6">
                {ideasData.length} ideas in total • {filteredIdeas.length} displayed
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Introduction and CTA to submit idea */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-gray-600 text-lg mb-8">
              Your input is crucial for shaping Fork:it. Share your ideas for new solutions, improvements, or features you'd like to see. Let's build a platform that truly serves your needs.
            </p>
            <Link 
              to="/deine-story"
              className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-md font-lexend font-medium text-base shadow-md transition-all"
            >
              Submit Your Idea
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default IdeasPage; 